// ============================================================
// GTFS Static (zip) loader
// ============================================================
// 都営バスの GTFS Static zip を取得・展開し、以下の lookup を提供する:
//   routeId → route_short_name (例: "184" → "都02")
//   tripId  → trip_headsign    (例: "...→渋谷駅前")
// 24h で localStorage キャッシュ。
//
// 解凍は `fflate` を使う (旧 jszip は `setImmediate` polyfill で `new Function`
// を含んでおり、Even Hub の審査で security risk として指摘されたため撤去)。

import { unzip, strFromU8 } from 'fflate'

export interface GtfsStaticData {
  routes: Record<string, { shortName: string; longName: string }>
  trips: Record<string, { headsign: string; routeId: string }>
  fetchedAt: number
}

const CACHE_KEY = 'gtfs-static-toei-v1'
const CACHE_TTL_MS = 24 * 60 * 60 * 1000  // 24h

export class GtfsStatic {
  private data: GtfsStaticData = { routes: {}, trips: {}, fetchedAt: 0 }
  private loadingPromise: Promise<void> | null = null

  /** zip URL から GTFS Static をロード。キャッシュが新しいなら fetch をスキップ。 */
  async load(zipUrl: string): Promise<void> {
    if (this.loadingPromise) return this.loadingPromise
    this.loadingPromise = (async () => {
      // 1. キャッシュチェック
      const cached = this.readCache()
      if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
        this.data = cached
        return
      }
      // 2. fetch + parse
      const res = await fetch(zipUrl)
      if (!res.ok) throw new Error(`GTFS Static fetch failed: ${res.status}`)
      const buf = new Uint8Array(await res.arrayBuffer())

      // fflate で非同期解凍。GTFS Static は数十 MB あるが routes.txt / trips.txt
      // 以外は使わないので filter で必要な 2 ファイルだけ展開する。
      const files = await new Promise<Record<string, Uint8Array>>((resolve, reject) => {
        unzip(
          buf,
          {
            filter: (f) => f.name === 'routes.txt' || f.name === 'trips.txt',
          },
          (err, out) => {
            if (err) reject(err)
            else resolve(out)
          },
        )
      })
      const routesBin = files['routes.txt']
      const tripsBin = files['trips.txt']
      if (!routesBin) throw new Error('routes.txt not found in GTFS zip')
      if (!tripsBin) throw new Error('trips.txt not found in GTFS zip')

      this.data = {
        routes: this.parseRoutes(strFromU8(routesBin)),
        trips: this.parseTrips(strFromU8(tripsBin)),
        fetchedAt: Date.now(),
      }
      this.writeCache(this.data)
    })()
    return this.loadingPromise
  }

  /** route_id → 友好的な系統名 (route_short_name) */
  getRouteName(routeId: string): string | undefined {
    return this.data.routes[routeId]?.shortName
  }

  getRouteLongName(routeId: string): string | undefined {
    return this.data.routes[routeId]?.longName
  }

  /** trip_id → 行き先 (trip_headsign) */
  getHeadsign(tripId: string): string | undefined {
    return this.data.trips[tripId]?.headsign
  }

  /** trip_id → route_id (TripUpdate に routeId が無いとき用) */
  getRouteIdForTrip(tripId: string): string | undefined {
    return this.data.trips[tripId]?.routeId
  }

  routeCount(): number { return Object.keys(this.data.routes).length }
  tripCount(): number { return Object.keys(this.data.trips).length }

  // ───────── private ─────────

  /** routes.txt:  route_id, agency_id, route_short_name, route_long_name, route_type, ... */
  private parseRoutes(csv: string): Record<string, { shortName: string; longName: string }> {
    const { headers, rows } = parseCsv(csv)
    const idxId = headers.indexOf('route_id')
    const idxShort = headers.indexOf('route_short_name')
    const idxLong = headers.indexOf('route_long_name')
    const out: Record<string, { shortName: string; longName: string }> = {}
    for (const row of rows) {
      const id = row[idxId] ?? ''
      if (!id) continue
      out[id] = {
        shortName: idxShort >= 0 ? (row[idxShort] ?? '') : '',
        longName:  idxLong  >= 0 ? (row[idxLong]  ?? '') : '',
      }
    }
    return out
  }

  /** trips.txt:  route_id, service_id, trip_id, trip_headsign, direction_id, ... */
  private parseTrips(csv: string): Record<string, { headsign: string; routeId: string }> {
    const { headers, rows } = parseCsv(csv)
    const idxId = headers.indexOf('trip_id')
    const idxHead = headers.indexOf('trip_headsign')
    const idxRoute = headers.indexOf('route_id')
    const out: Record<string, { headsign: string; routeId: string }> = {}
    for (const row of rows) {
      const id = row[idxId] ?? ''
      if (!id) continue
      out[id] = {
        headsign: idxHead  >= 0 ? (row[idxHead]  ?? '') : '',
        routeId:  idxRoute >= 0 ? (row[idxRoute] ?? '') : '',
      }
    }
    return out
  }

  private readCache(): GtfsStaticData | null {
    try {
      const raw = localStorage.getItem(CACHE_KEY)
      if (!raw) return null
      return JSON.parse(raw) as GtfsStaticData
    } catch {
      return null
    }
  }

  private writeCache(data: GtfsStaticData): void {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(data))
    } catch (err) {
      console.warn('[gtfs-static] cache write failed (quota?)', err)
    }
  }
}

// ───────── 簡易 CSV パーサ ─────────
// GTFS CSV は基本シンプル (改行・カンマ・ダブルクオート) だけ対応する。
function parseCsv(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.replace(/\r\n/g, '\n').split('\n').filter((l) => l.length > 0)
  if (lines.length === 0) return { headers: [], rows: [] }
  const headers = splitCsvLine(lines[0])
  const rows = lines.slice(1).map(splitCsvLine)
  return { headers, rows }
}

function splitCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let inQuote = false
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (inQuote) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++ }
      else if (c === '"') { inQuote = false }
      else { cur += c }
    } else {
      if (c === '"') { inQuote = true }
      else if (c === ',') { out.push(cur); cur = '' }
      else { cur += c }
    }
  }
  out.push(cur)
  return out
}
